// V2.4/V2.2/V2.1 (observability-completion) — pure formatting for the Health page.
//
// All display logic that turns the GET /api/observability/{health,voice-latency}
// payloads into rendered strings/rows lives here so it is unit-testable without a
// DOM (mirrors evalRunFormat.js). The React component (HealthPage.jsx) is a thin
// renderer over these.

// ── duration + number formatting ──────────────────────────────────────

// A latency in ms → "280 ms" under 1s, "9.6 s" above. null → "—".
export function fmtDuration(ms) {
  if (ms == null || Number.isNaN(ms)) return "—";
  if (ms < 1000) return `${Math.round(ms)} ms`;
  return `${(ms / 1000).toFixed(1)} s`;
}

// coverage fraction 0..1 → "91.4" (percent, one decimal). null → "—".
export function fmtPct(fraction) {
  if (fraction == null || Number.isNaN(fraction)) return "—";
  return (fraction * 100).toFixed(1);
}

// seconds-ago → "4m 20s" / "45s". null → "—".
export function fmtAgo(seconds) {
  if (seconds == null || Number.isNaN(seconds)) return "—";
  const s = Math.max(0, Math.round(seconds));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const rem = s % 60;
  return rem ? `${m}m ${rem}s` : `${m}m`;
}

// ── health strip states ────────────────────────────────────────────────

// A backend probe result → {state, label} for the tile pill.
export function backendState(res) {
  if (!res || res.connected === false) {
    const label = res && res.status === "unreachable" ? "UNREACHABLE" : "DOWN";
    return { state: "crit", label };
  }
  return { state: "ok", label: "HEALTHY" };
}

// Trace-link coverage vs the 100% SLO → tile pill state.
export function coverageState(overall, slo = 1.0) {
  if (overall == null) return { state: "dim", label: "no data" };
  return overall < slo
    ? { state: "warn", label: "below SLO" }
    : { state: "ok", label: "at SLO" };
}

// Last-voice-metric age vs the alert-on-absence threshold (default 180s).
export function metricAgeState(seconds, thresholdS = 180) {
  if (seconds == null) return { state: "dim" };
  return seconds > thresholdS ? { state: "crit" } : { state: "ok" };
}

// A single health payload → the ordered list of backend tiles to render.
const BACKEND_META = [
  { key: "openobserve", name: "OpenObserve", meta: "metrics store" },
  { key: "langfuse", name: "Langfuse", meta: "LLM trace UI" },
  { key: "clickhouse", name: "ClickHouse", meta: "trace store" },
  { key: "otel_collector", name: "OTel Collector", meta: "ingest gateway" },
];

export function backendTiles(backends) {
  const b = backends || {};
  return BACKEND_META.map((m) => {
    const res = b[m.key];
    return { ...m, res: res || null, ...backendState(res) };
  });
}

// ── voice-latency waterfall ────────────────────────────────────────────

// TTFA components in caller-experienced order, with platform colors + copy.
export const WATERFALL_META = [
  { key: "eou_delay", label: "EOU detection", sub: "speech-end → turn ready", color: "#3B3BFF" },
  { key: "transcription_delay", label: "Transcription", sub: "STT final transcript", color: "#6366F1" },
  { key: "ttft", label: "Time to first token", sub: "LLM first token (incl. routing on a supervisor)", color: "#F59E0B" },
  { key: "ttfb", label: "TTFB", sub: "TTS first byte", color: "#10B981" },
];

// Pick the representative latency for a component: p50, else avg, else null.
export function repMs(stat) {
  if (!stat) return null;
  if (stat.p50 != null) return stat.p50;
  if (stat.avg != null) return stat.avg;
  return null;
}

// Build the waterfall rows: each component's representative ms + p95 + the
// cumulative left offset + width as a % of the estimated TTFA total, so the
// segmented bar lines up. Components with no data get ms=null (rendered "—",
// zero width). Returns {rows, totalMs} where totalMs = Σ representative ms
// (an estimate of median TTFA — labeled as such in the UI, since we emit
// per-component histograms, not a single TTFA metric).
export function waterfallRows(waterfall) {
  const wf = waterfall || {};
  const raw = WATERFALL_META.map((m) => {
    const stat = wf[m.key] || null;
    return { ...m, ms: repMs(stat), p95: stat ? stat.p95 : null, count: stat ? stat.count : 0 };
  });
  const totalMs = raw.reduce((acc, r) => acc + (r.ms || 0), 0);
  let cursor = 0;
  const rows = raw.map((r) => {
    const widthPct = totalMs > 0 && r.ms ? (r.ms / totalMs) * 100 : 0;
    const leftPct = cursor;
    cursor += widthPct;
    return { ...r, widthPct, leftPct };
  });
  return { rows, totalMs };
}

// Turn the experts[] breakdown into sorted, formatted cards (slowest p95 first
// — already sorted server-side, but be defensive). Empty-safe.
export function expertCards(experts) {
  if (!Array.isArray(experts)) return [];
  return [...experts]
    .sort((a, b) => (b.p95 ?? -1) - (a.p95 ?? -1))
    .map((e) => ({
      expertId: e.expert_id,
      p50: e.p50,
      p95: e.p95,
      count: e.count || 0,
    }));
}

// True when the agent is a supervisor (drives the expert-breakdown + banner).
export function isSupervisor(payload) {
  return !!payload && payload.agent_kind === "supervisor";
}
